import { z } from 'zod';

const safeIdentifierSchema = z.string().min(1).max(120).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);
const humanTextSchema = z.string().min(1).max(240);
const jurisdictionTagSchema = z.string().regex(/^(?:ALL-US|NON-CANNABIS|US-[A-Z]{2})$/);

export const reviewDueProofPickerContextSchema = z.object({
  workflow: z.literal('review_due'),
  policy: z.literal('approved_reference'),
  reviewType: z.enum([
    'improvement_campaign_readout',
    'stabilization',
    'qbr',
    'renewal_planning',
    'at_risk_review',
  ]),
  locationId: z.string().uuid(),
}).strict();

export const contentProofPickerContextSchema = z.object({
  workflow: z.literal('content'),
  policy: z.literal('approved_reference'),
  channel: z.enum(['linkedin', 'instagram']),
  targetState: z.enum(['draft', 'approved', 'scheduled', 'published', 'killed']),
  jurisdictionTag: jurisdictionTagSchema,
}).strict();

export const leadProofPickerContextSchema = z.object({
  workflow: z.literal('lead'),
  policy: z.literal('cold_dispensary_outreach'),
  leadType: z.literal('dispensary'),
  locationId: z.string().uuid().optional(),
}).strict();

export const proofPickerContextSchema = z.discriminatedUnion('workflow', [
  reviewDueProofPickerContextSchema,
  contentProofPickerContextSchema,
  leadProofPickerContextSchema,
]);

const proofPickerSummaryBase = {
  proofMetricId: safeIdentifierSchema,
  version: z.number().int().positive(),
  label: humanTextSchema,
  value: humanTextSchema,
  sourceLabel: z.string().min(1).max(120),
  valueAreas: z.array(safeIdentifierSchema).max(50),
};

export const proofPickerSummarySchema = z.discriminatedUnion('policy', [
  z.object({
    ...proofPickerSummaryBase,
    policy: z.literal('approved_reference'),
  }).strict(),
  z.object({
    ...proofPickerSummaryBase,
    policy: z.literal('cold_dispensary_outreach'),
    approvedClaim: z.string().min(1).max(2_000),
    jurisdictionTag: jurisdictionTagSchema,
  }).strict(),
]);

export const proofPickerBlockerCodeSchema = z.enum([
  'ambiguous_family_version',
  'invalid_family_version',
  'not_approved',
  'expired',
  'revoked',
  'missing_source_ref',
  'missing_value_area',
  'missing_outreach_channel',
  'missing_outreach_audit',
  'outreach_revoked',
  'wrong_jurisdiction',
  'unknown_target_state',
  'invalid_non_cannabis_jurisdiction',
  'named_without_permission',
  'missing_claim_text',
  'unsupported_claim_variant',
  'missing_source_verification',
]);

export const proofPickerBlockerSchema = z.object({
  code: proofPickerBlockerCodeSchema,
  message: z.string().min(1).max(240),
}).strict();

export const proofPickerHydrationResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('eligible'),
    proofMetricId: safeIdentifierSchema,
    summary: proofPickerSummarySchema,
  }).strict(),
  z.object({
    status: z.literal('blocked'),
    proofMetricId: safeIdentifierSchema,
    blockers: z.array(proofPickerBlockerSchema).min(1).max(20),
  }).strict(),
  z.object({
    status: z.literal('unresolved'),
    proofMetricId: safeIdentifierSchema,
    message: z.literal('Proof is unavailable.'),
  }).strict(),
]);

export const reviewDueProofPickerListResponseSchema = z.object({
  ok: z.literal(true),
  items: z.array(proofPickerSummarySchema).max(200),
}).strict();

export const reviewDueProofPickerHydrationResponseSchema = z.object({
  ok: z.literal(true),
  eligibleSummaries: z.array(proofPickerSummarySchema).max(200),
  hydratedSelection: z.array(proofPickerHydrationResultSchema).max(100),
}).strict();

export const contentProofPickerListResponseSchema = z.object({
  ok: z.literal(true),
  items: z.array(proofPickerSummarySchema).max(200),
}).strict();

export const contentProofPickerHydrationResponseSchema = z.object({
  ok: z.literal(true),
  eligibleSummaries: z.array(proofPickerSummarySchema).max(200),
  hydratedSelection: z.array(proofPickerHydrationResultSchema).max(100),
}).strict();

export const proofPickerConsumerReferenceSchema = z.discriminatedUnion('consumerType', [
  z.object({ consumerType: z.literal('review_due'), consumerId: z.string().uuid() }).strict(),
  z.object({ consumerType: z.literal('content'), consumerId: z.string().uuid() }).strict(),
  z.object({ consumerType: z.literal('lead'), consumerId: z.string().uuid() }).strict(),
]);

const storedProofFamilyIdsSchema = z.array(safeIdentifierSchema).max(100).superRefine((values, context) => {
  if (new Set(values).size !== values.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Proof family identities must be unique.' });
  }
});

export const authorizedProofPickerConsumerSchema = z.discriminatedUnion('consumerType', [
  z.object({
    consumerType: z.literal('review_due'),
    consumerId: z.string().uuid(),
    context: reviewDueProofPickerContextSchema,
    proofMetricIds: storedProofFamilyIdsSchema,
  }).strict(),
  z.object({
    consumerType: z.literal('content'),
    consumerId: z.string().uuid(),
    context: contentProofPickerContextSchema,
    proofMetricIds: storedProofFamilyIdsSchema,
  }).strict(),
  z.object({
    consumerType: z.literal('lead'),
    consumerId: z.string().uuid(),
    context: leadProofPickerContextSchema,
    proofMetricIds: storedProofFamilyIdsSchema,
    targetState: z.string().regex(/^[A-Z]{2}$/).nullable(),
  }).strict(),
]);

export type ProofPickerContext = z.infer<typeof proofPickerContextSchema>;
export type ProofPickerSummary = z.infer<typeof proofPickerSummarySchema>;
export type ProofPickerBlockerCode = z.infer<typeof proofPickerBlockerCodeSchema>;
export type ProofPickerBlocker = z.infer<typeof proofPickerBlockerSchema>;
export type ProofPickerHydrationResult = z.infer<typeof proofPickerHydrationResultSchema>;
export type ReviewDueProofPickerListResponse = z.infer<typeof reviewDueProofPickerListResponseSchema>;
export type ReviewDueProofPickerHydrationResponse = z.infer<typeof reviewDueProofPickerHydrationResponseSchema>;
export type ContentProofPickerListResponse = z.infer<typeof contentProofPickerListResponseSchema>;
export type ContentProofPickerHydrationResponse = z.infer<typeof contentProofPickerHydrationResponseSchema>;
export type ProofPickerConsumerReference = z.infer<typeof proofPickerConsumerReferenceSchema>;
export type AuthorizedProofPickerConsumer = z.infer<typeof authorizedProofPickerConsumerSchema>;

export function proofPickerContextKey(input: unknown): string {
  const context = proofPickerContextSchema.parse(input);
  if (context.workflow === 'review_due') {
    return JSON.stringify([
      'proof-picker/v1',
      context.workflow,
      context.policy,
      context.reviewType,
      context.locationId.toLowerCase(),
    ]);
  }
  if (context.workflow === 'content') {
    return JSON.stringify([
      'proof-picker/v1',
      context.workflow,
      context.policy,
      context.channel,
      context.targetState,
      context.jurisdictionTag,
    ]);
  }
  return JSON.stringify([
    'proof-picker/v1',
    context.workflow,
    context.policy,
    context.leadType,
    context.locationId?.toLowerCase() ?? null,
  ]);
}
